import React from 'react';
import { JobApplication } from '../../../providers/api/dtos';
import { ComputedJobApplicationStatus, JobApplicationStatus } from '../../../domain/types';
import useRBAC from '../../../hooks/use-rbac/useRBAC';

type UseComputedJobApplicationStatusResult = {
  status: ComputedJobApplicationStatus,
  canBeUpdated: boolean,
  canBeUpdatedTo: (status: ComputedJobApplicationStatus) => boolean,
};

const adminAndHrAllowedStatusUpdates: Record<ComputedJobApplicationStatus, ComputedJobApplicationStatus[]> = {
  [JobApplicationStatus.IN_PROGRESS]: [
    JobApplicationStatus.ASSIGNED,
    JobApplicationStatus.INTERVIEW_SCHEDULED,
    JobApplicationStatus.TALENT_POOL,
    JobApplicationStatus.KO_NOT_INTERESTED,
    JobApplicationStatus.KO_WRONG_NUMBER,
    JobApplicationStatus.KO_NOT_ELIGIBLE,
    JobApplicationStatus.KO_DUPLICATE,
    JobApplicationStatus.KO_TEST,
  ],
  MANAGED: [
    JobApplicationStatus.ASSIGNED,
    JobApplicationStatus.INTERVIEW_SCHEDULED,
    JobApplicationStatus.TALENT_POOL,
    JobApplicationStatus.KO_NOT_INTERESTED,
    JobApplicationStatus.KO_WRONG_NUMBER,
    JobApplicationStatus.KO_NOT_ELIGIBLE,
    JobApplicationStatus.KO_DUPLICATE,
    JobApplicationStatus.KO_TEST,
  ],
  [JobApplicationStatus.ASSIGNED]: [
    JobApplicationStatus.INTERVIEW_SCHEDULED,
    JobApplicationStatus.OFFER,
    JobApplicationStatus.TALENT_POOL,
    JobApplicationStatus.KO_NOT_INTERESTED,
    JobApplicationStatus.KO_NOT_ELIGIBLE,
    JobApplicationStatus.KO_DUPLICATE,
  ],
  [JobApplicationStatus.INTERVIEW_SCHEDULED]: [
    JobApplicationStatus.OFFER,
    JobApplicationStatus.TALENT_POOL,
    JobApplicationStatus.KO_NOT_INTERESTED,
    JobApplicationStatus.KO_NOT_ELIGIBLE,
    JobApplicationStatus.KO_DUPLICATE,
  ],
  [JobApplicationStatus.OFFER]: [
    JobApplicationStatus.HIRED,
    JobApplicationStatus.KO_OFFER_REFUSED,
  ],
  [JobApplicationStatus.HIRED]: [],
  [JobApplicationStatus.TALENT_POOL]: [JobApplicationStatus.IN_PROGRESS],
  [JobApplicationStatus.KO_UNREACHABLE]: [JobApplicationStatus.IN_PROGRESS],
  [JobApplicationStatus.KO_NOT_INTERESTED]: [JobApplicationStatus.IN_PROGRESS],
  [JobApplicationStatus.KO_OFFER_REFUSED]: [JobApplicationStatus.IN_PROGRESS],
  [JobApplicationStatus.KO_WRONG_NUMBER]: [JobApplicationStatus.IN_PROGRESS],
  [JobApplicationStatus.KO_NOT_ELIGIBLE]: [],
  [JobApplicationStatus.KO_DUPLICATE]: [],
  [JobApplicationStatus.KO_TEST]: [],
};

const assigneeAllowedStatusUpdates: Record<ComputedJobApplicationStatus, ComputedJobApplicationStatus[]> = {
  [JobApplicationStatus.IN_PROGRESS]: [],
  MANAGED: [],
  [JobApplicationStatus.ASSIGNED]: [],
  [JobApplicationStatus.INTERVIEW_SCHEDULED]: [
    JobApplicationStatus.TALENT_POOL,
    JobApplicationStatus.KO_NOT_INTERESTED,
    JobApplicationStatus.KO_NOT_ELIGIBLE,
  ],
  [JobApplicationStatus.OFFER]: [],
  [JobApplicationStatus.HIRED]: [],
  [JobApplicationStatus.TALENT_POOL]: [JobApplicationStatus.IN_PROGRESS],
  [JobApplicationStatus.KO_UNREACHABLE]: [JobApplicationStatus.IN_PROGRESS],
  [JobApplicationStatus.KO_NOT_INTERESTED]: [JobApplicationStatus.IN_PROGRESS],
  [JobApplicationStatus.KO_OFFER_REFUSED]: [JobApplicationStatus.IN_PROGRESS],
  [JobApplicationStatus.KO_WRONG_NUMBER]: [JobApplicationStatus.IN_PROGRESS],
  [JobApplicationStatus.KO_NOT_ELIGIBLE]: [],
  [JobApplicationStatus.KO_DUPLICATE]: [],
  [JobApplicationStatus.KO_TEST]: [],
};

export function computeAllowedJobApplicationStatusUpdates(
  isAdminOrHR: boolean,
  isAssignedAgent: boolean,
  status: ComputedJobApplicationStatus,
): ComputedJobApplicationStatus[] {
  if (isAdminOrHR) {
    return adminAndHrAllowedStatusUpdates[status];
  }

  if (isAssignedAgent) {
    return assigneeAllowedStatusUpdates[status];
  }

  return [];
}

export function useComputedJobApplicationStatus(jobApplication: JobApplication): UseComputedJobApplicationStatusResult {
  const { userIsAdmin, userIsHR, user } = useRBAC();

  const computedStatus = React.useMemo(() => {
    if (jobApplication.status === JobApplicationStatus.IN_PROGRESS && !!jobApplication.supervisorEmail) {
      return 'MANAGED';
    }

    return jobApplication.status;
  }, [jobApplication.status, jobApplication.supervisorEmail]);

  const canBeUpdatedTo = React.useMemo(
    () => computeAllowedJobApplicationStatusUpdates(
      userIsAdmin || userIsHR,
      !!jobApplication.assigneeEmail && jobApplication.assigneeEmail === user?.email,
      computedStatus,
    ),
    [computedStatus, jobApplication.assigneeEmail, user?.email, userIsAdmin, userIsHR],
  );

  return {
    status: computedStatus,
    canBeUpdated: canBeUpdatedTo.length > 0,
    canBeUpdatedTo: (s: ComputedJobApplicationStatus) => canBeUpdatedTo.includes(s),
  };
}
